import { Suspense } from 'react'
import { appLink } from '@app/config/appLink'
import { ProtectedRoute } from '@app/Auth/ProtectedRoute'
import { FEATURE_FLAGS } from '@app/app-constants'
import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { UnauthorizedPage } from '@app/routes/UnauthorisedPage/UnauthorizedPage'
import PropertyLayout from '@app/layout/PropertyLayout'
import { PropertyLandingPage } from '@app/components/Property/components/PropertyLandingPage'
import { AccessLandingPage } from '@app/routes/AccessPage'
import { AdaptationPage } from '@app/routes/AdaptationPage'
import { AdminLayout } from '@app/modules/Admin/layouts/Admin.layout'
import ManageUsersPage from '@app/modules/Admin/pages/Admin.page'
import { FeatureFlagsPage } from '@app/modules/Admin/pages/FeatureFlagsPage'
import { OnboardingPage } from '@app/routes/OnboardingPage'
import { PropertyPage } from '@app/routes/PropertyPage'
import { ErrorPage } from '@app/routes/ErrorPage'
import { Navigate, Outlet, Route } from 'react-router-dom'
import { LayerPermissionsPage } from '@app/modules/Admin/pages/Admin.layerPermissions'
import SmokeTestShadCn from '@app/SmokeTestShadCn'
import { RexLivePage } from '@app/modules/Live/Live.page'
import LiveLayout from '@app/modules/Live/Live.layout'
import { DocumentsLibraryPage } from '@app/modules/Documents/pages/documents-library.page'
import {
  WorkspacesDialog,
  WorkspacesManager,
  WorkspacesCreate,
  WorkspaceEdit,
} from './lazyWorkspaces'
import { withSuspense } from '@app/lib/withSuspense'

export default function AppRoutes() {
  return (
    <>
      <Route
        path="smoke"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.SHADCN_UI}
            fallback={<UnauthorizedPage feature={appLink.smokeShadCn.featureFlag} />}
          >
            <SmokeTestShadCn />
          </FeatureFlagGate>
        }
      />

      <Route path="property" element={<Navigate to="property/search" replace />} />

      <Route
        path="property/search"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.PROPERTY_PAGE}
            fallback={<UnauthorizedPage feature={appLink.property.featureFlag} />}
          >
            <ProtectedRoute>
              <PropertyLayout>
                <PropertyLandingPage />
              </PropertyLayout>
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      <Route
        path="property/:id"
        element={
          <ProtectedRoute>
            <PropertyPage />
            <Outlet />
          </ProtectedRoute>
        }
      />

      <Route path={appLink.access.path} element={<AccessLandingPage />} />

      <Route
        path="adaptation"
        element={
          <ProtectedRoute>
            <AdaptationPage />
          </ProtectedRoute>
        }
      />

      <Route path="onboarding" element={<OnboardingPage />} />

      <Route
        path="admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="manage-users" replace />} />
        <Route path="manage-users" element={<ManageUsersPage />} />
        <Route path="feature-flags" element={<FeatureFlagsPage />} />
        <Route path="layer-permissions" element={<LayerPermissionsPage />} />
      </Route>

      <Route
        path={appLink.documents.path}
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.EVIDENCE_LIBRARY}
            fallback={<UnauthorizedPage feature={appLink.documents.featureFlag} />}
          >
            <ProtectedRoute>
              <DocumentsLibraryPage />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      {/* Same page as above — an id-scoped shorthand (a "Share as a link" URL, or the table's
          Title link) that opens the library with that document's preview open. */}
      <Route
        path={`${appLink.documents.path}/:id`}
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.EVIDENCE_LIBRARY}
            fallback={<UnauthorizedPage feature={appLink.documents.featureFlag} />}
          >
            <ProtectedRoute>
              <DocumentsLibraryPage />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      <Route path="unauthorized" element={<UnauthorizedPage />} />
      <Route path="*" element={<ErrorPage />} />

      <Route
        path={appLink.rexLive.path}
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.REX_LIVE}
            fallback={<UnauthorizedPage feature={appLink.rexLive.featureFlag} />}
          >
            <ProtectedRoute>
              <LiveLayout>
                <RexLivePage />
              </LiveLayout>
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      >
        <Route path="workspaces" element={withSuspense(<WorkspacesDialog />)}>
          <Route path="saved" element={withSuspense(<WorkspacesManager />)} />
          <Route path="create" element={withSuspense(<WorkspacesCreate />)} />
          <Route path=":workspaceId/edit" element={withSuspense(<WorkspaceEdit />)} />
        </Route>
      </Route>
    </>
  )
}
